import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';


import { trackFormSubmission, trackLeadGeneration } from 'utils/analytics';
import { Checkbox } from '../../components/ui/Checkbox';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';






const COPAFRegistrationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    country: '',
    city: '',

    // Professional Background
    industryExperience: '',
    currentRole: '',
    organizationSize: '',
    yearsExperience: '',

    // COPAF Specialization
    specializations: [],
    certificationGoals: [],
    membershipTier: '',

    // Additional Information
    previousCertifications: '',
    motivations: '',
    expectations: '',

    // Agreements
    acceptTerms: false,
    acceptMarketing: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const membershipTiers = [
  {
    id: 'individual',
    name: 'Adhésion Individuelle',
    price: '250€/an',
    benefits: ['Accès aux publications COPAF', 'Webinaires mensuels', 'Networking digital', 'Support email']
  },
  {
    id: 'corporate',
    name: 'Adhésion Entreprise',
    price: '1,500€/an',
    benefits: ['Accès pour 5 collaborateurs', 'Formations exclusives', 'Consulting personnalisé', 'Support prioritaire']
  },
  {
    id: 'premium',
    name: 'Adhésion Premium',
    price: '500€/an',
    benefits: ['Accès VIP aux événements', 'Mentoring personnel', 'Certification accélérée', 'Support téléphonique']
  }];


  const specializations = [
  'Gestion Portuaire',
  'Logistique Maritime',
  'Technologies Portuaires',
  'Développement Durable',
  'Sécurité Maritime',
  'Commerce International',
  'Infrastructure Portuaire',
  'Innovation Digitale'];


  const certificationGoals = [
  'Certification Manager Portuaire',
  'Expert en Logistique Maritime',
  'Spécialiste Technologies Portuaires',
  'Consultant en Développement Durable',
  'Auditeur Sécurité Maritime'];


  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSpecializationChange = (specialization, checked) => {
    setFormData((prev) => ({
      ...prev,
      specializations: checked ?
      [...prev?.specializations, specialization] :
      prev?.specializations?.filter((s) => s !== specialization)
    }));
  };

  const handleCertificationGoalChange = (goal, checked) => {
    setFormData((prev) => ({
      ...prev,
      certificationGoals: checked ?
      [...prev?.certificationGoals, goal] :
      prev?.certificationGoals?.filter((g) => g !== goal)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.firstName) newErrors.firstName = 'Prénom requis';
        if (!formData?.lastName) newErrors.lastName = 'Nom requis';
        if (!formData?.email) newErrors.email = 'Email requis';
        if (!formData?.phone) newErrors.phone = 'Téléphone requis';
        break;
      case 2:
        if (!formData?.company) newErrors.company = 'Entreprise requise';
        if (!formData?.position) newErrors.position = 'Poste requis';
        if (!formData?.country) newErrors.country = 'Pays requis';
        break;
      case 3:
        if (!formData?.industryExperience) newErrors.industryExperience = 'Expérience requise';
        if (!formData?.yearsExperience) newErrors.yearsExperience = 'Années d\'expérience requises';
        break;
      case 4:
        if (formData?.specializations?.length === 0) newErrors.specializations = 'Sélectionnez au moins une spécialisation';
        if (!formData?.membershipTier) newErrors.membershipTier = 'Type d\'adhésion requis';
        break;
      case 5:
        if (!formData?.acceptTerms) newErrors.acceptTerms = 'Acceptation des conditions requise';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      // Track form submission
      trackFormSubmission('copaf_registration_form', {
        user_name: `${formData?.firstName} ${formData?.lastName}`,
        user_email: formData?.email,
        user_phone: formData?.phone,
        user_company: formData?.company,
        registration_type: formData?.registrationType,
        payment_method: formData?.paymentMethod
      });

      // Track lead generation
      trackLeadGeneration('copaf_registration', {
        email: formData?.email,
        company: formData?.company,
        name: `${formData?.firstName} ${formData?.lastName}`,
        registration_type: formData?.registrationType
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Success - redirect to success page or show confirmation
      alert('Inscription COPAF soumise avec succès! Vous recevrez un email de confirmation sous peu.');
      navigate('/');

    } catch (error) {
      alert('Erreur lors de l\'inscription. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Informations Personnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Prénom *"
                value={formData?.firstName}
                onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                error={errors?.firstName}
                placeholder="Votre prénom" />

              <Input
                label="Nom *"
                value={formData?.lastName}
                onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                error={errors?.lastName}
                placeholder="Votre nom" />

              <Input
                label="Email *"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                placeholder="votre@email.com" />

              <Input
                label="Téléphone *"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                error={errors?.phone}
                placeholder="+33 1 23 45 67 89" />

            </div>
          </div>);


      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Informations Professionnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Entreprise/Organisation *"
                value={formData?.company}
                onChange={(e) => handleInputChange('company', e?.target?.value)}
                error={errors?.company}
                placeholder="Nom de votre entreprise" />

              <Input
                label="Poste Actuel *"
                value={formData?.position}
                onChange={(e) => handleInputChange('position', e?.target?.value)}
                error={errors?.position}
                placeholder="Votre fonction" />

              <Select
                label="Pays *"
                value={formData?.country}
                onChange={(value) => handleInputChange('country', value)}
                error={errors?.country}
                options={[
                { value: 'france', label: 'France' },
                { value: 'senegal', label: 'Sénégal' },
                { value: 'maroc', label: 'Maroc' },
                { value: 'cote-divoire', label: 'Côte d\'Ivoire' },
                { value: 'cameroun', label: 'Cameroun' },
                { value: 'other', label: 'Autre' }]
                }
                placeholder="Sélectionnez votre pays" />

              <Input
                label="Ville"
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                placeholder="Votre ville" />

            </div>
          </div>);


      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Expérience Professionnelle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Secteur d'Activité *"
                value={formData?.industryExperience}
                onChange={(value) => handleInputChange('industryExperience', value)}
                error={errors?.industryExperience}
                options={[
                { value: 'port-management', label: 'Gestion Portuaire' },
                { value: 'maritime-logistics', label: 'Logistique Maritime' },
                { value: 'shipping', label: 'Transport Maritime' },
                { value: 'customs', label: 'Douanes' },
                { value: 'consulting', label: 'Conseil' },
                { value: 'technology', label: 'Technologies' },
                { value: 'other', label: 'Autre' }]
                }
                placeholder="Sélectionnez votre secteur" />

              <Select
                label="Années d'Expérience *"
                value={formData?.yearsExperience}
                onChange={(value) => handleInputChange('yearsExperience', value)}
                error={errors?.yearsExperience}
                options={[
                { value: '0-2', label: '0-2 ans' },
                { value: '3-5', label: '3-5 ans' },
                { value: '6-10', label: '6-10 ans' },
                { value: '11-15', label: '11-15 ans' },
                { value: '15+', label: 'Plus de 15 ans' }]
                }
                placeholder="Sélectionnez votre expérience" />

              <Select
                label="Taille de l'Organisation"
                value={formData?.organizationSize}
                onChange={(value) => handleInputChange('organizationSize', value)}
                options={[
                { value: 'startup', label: 'Startup (1-10)' },
                { value: 'small', label: 'PME (11-50)' },
                { value: 'medium', label: 'ETI (51-250)' },
                { value: 'large', label: 'Grande entreprise (250+)' }]
                }
                placeholder="Taille de votre organisation" />

              <Input
                label="Rôle Actuel"
                value={formData?.currentRole}
                onChange={(e) => handleInputChange('currentRole', e?.target?.value)}
                placeholder="Décrivez votre rôle" />

            </div>
          </div>);


      case 4:
        return (
          <div className="space-y-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Spécialisations et Objectifs COPAF</h3>
            {/* Specializations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Domaines de Spécialisation * {errors?.specializations && <span className="text-red-500 text-xs">({errors?.specializations})</span>}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {specializations?.map((specialization) =>
                <Checkbox
                  key={specialization}
                  label={specialization}
                  checked={formData?.specializations?.includes(specialization)}
                  onChange={(checked) => handleSpecializationChange(specialization, checked)} />

                )}
              </div>
            </div>
            {/* Certification Goals */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Objectifs de Certification
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {certificationGoals?.map((goal) =>
                <Checkbox
                  key={goal}
                  label={goal}
                  checked={formData?.certificationGoals?.includes(goal)}
                  onChange={(checked) => handleCertificationGoalChange(goal, checked)} />

                )}
              </div>
            </div>
            {/* Membership Tier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Type d'Adhésion * {errors?.membershipTier && <span className="text-red-500 text-xs">({errors?.membershipTier})</span>}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {membershipTiers?.map((tier) =>
                <div
                  key={tier?.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData?.membershipTier === tier?.id ?
                  'border-[#D93B34] bg-red-50' :
                  'border-gray-200 hover:border-gray-300'}`
                  }
                  onClick={() => handleInputChange('membershipTier', tier?.id)}>

                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{tier?.name}</h4>
                      <span className="text-lg font-bold text-[#D93B34]">{tier?.price}</span>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {tier?.benefits?.map((benefit, index) =>
                    <li key={index} className="flex items-start space-x-2">
                          <span className="text-green-500 text-xs mt-1">✓</span>
                          <span>{benefit}</span>
                        </li>
                    )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>);


      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Finalisation de l'Inscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certifications Précédentes
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D93B34] focus:border-transparent"
                  rows="3"
                  value={formData?.previousCertifications}
                  onChange={(e) => handleInputChange('previousCertifications', e?.target?.value)}
                  placeholder="Listez vos certifications existantes (optionnel)" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivations
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D93B34] focus:border-transparent"
                  rows="3"
                  value={formData?.motivations}
                  onChange={(e) => handleInputChange('motivations', e?.target?.value)}
                  placeholder="Qu'est-ce qui vous motive à rejoindre la COPAF?" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attentes
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D93B34] focus:border-transparent"
                  rows="3"
                  value={formData?.expectations}
                  onChange={(e) => handleInputChange('expectations', e?.target?.value)}
                  placeholder="Que souhaitez-vous retirer de votre adhésion COPAF?" />

              </div>
            </div>
            {/* Agreement Checkboxes */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              <Checkbox
                label="J'accepte les conditions générales d'utilisation et la politique de confidentialité de la COPAF *"
                checked={formData?.acceptTerms}
                onChange={(checked) => handleInputChange('acceptTerms', checked)}
                error={errors?.acceptTerms} />

              <Checkbox
                label="J'accepte de recevoir des communications marketing de la COPAF"
                checked={formData?.acceptMarketing}
                onChange={(checked) => handleInputChange('acceptMarketing', checked)} />

            </div>
          </div>);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-r from-[#D93B34] to-[#9B8E56] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <Image
                  src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                  alt="COPAF - Conférence des Ports Africains logo représentant l'excellence portuaire et maritime africaine"
                  className="w-full h-full object-contain" />

              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">Inscription COPAF</h1>
                <p className="text-red-100">Conférence des Ports Africains</p>
              </div>
            </div>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Rejoignez la communauté de professionnels dédiée à l'excellence portuaire et maritime africaine
            </p>
          </div>
        </div>
      </section>
      {/* Progress Indicator */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5]?.map((step) =>
            <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === currentStep ?
              'bg-[#D93B34] text-white' :
              step < currentStep ?
              'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`
              }>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 5 &&
              <div className={`w-20 h-1 mx-2 ${
              step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`
              } />
              }
              </div>
            )}
          </div>
          <div className="mt-4 text-center">
            <span className="text-sm text-gray-600">
              Étape {currentStep} sur 5: {
              currentStep === 1 ? 'Informations Personnelles' :
              currentStep === 2 ? 'Informations Professionnelles' :
              currentStep === 3 ? 'Expérience Professionnelle' :
              currentStep === 4 ? 'Spécialisations COPAF' : 'Finalisation'
              }
            </span>
          </div>
        </div>
      </section>
      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <form onSubmit={handleSubmit}>
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-2">

                  Précédent
                </Button>

                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="px-6 py-2">

                    Annuler
                  </Button>
                  
                  {currentStep < 5 ?
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="px-6 py-2 bg-[#D93B34] hover:bg-[#B8312A]">

                      Suivant
                    </Button> :

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-2 bg-gradient-to-r from-[#D93B34] to-[#9B8E56] hover:from-[#B8312A] hover:to-[#857542]">

                      {isSubmitting ? 'Inscription en cours...' : 'Finaliser l\'Inscription'}
                    </Button>
                  }
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
      {/* Benefits Preview */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Pourquoi Rejoindre la COPAF ?
            </h2>
            <p className="text-xl text-slate-600">
              Découvrez les avantages exclusifs de notre communauté
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-[#D93B34] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Partage d'Expertise et Innovation</h3>
              <p className="text-slate-600">Échange de connaissances et innovations technologiques et digitales pour l'excellence portuaire africaine

              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-[#9B8E56] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Networking International</h3>
              <p className="text-slate-600">
                Rejoignez un réseau de plus de 2,000 professionnels portuaires à travers l'Afrique
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-[#D93B34] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Harmonisation et Intégration Régionale</h3>
              <p className="text-slate-600">Travail sur l'alignement des stratégies portuaires et des cadres réglementaires pour améliorer la connectivité maritime et logistique sur le continent.

              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

};

export default COPAFRegistrationForm;